import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "components/common";
import { getPaymentRequest, clearPaymentRequest } from "redux/erp/paymentRequestSlice";
import { PAYMENT_REQUEST_FORM_ISO } from "app_config";

const money = (v) => (Number(v) || 0).toLocaleString("en-US");

const SignatureBlock = ({ label, name, position, date, signatureUrl }) => (
  <div style={{ border: "1px solid #000", padding: 8, minHeight: 150, flex: 1 }}>
    <div style={{ fontWeight: 600 }}>{label}</div>
    <div style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {signatureUrl && <img src={signatureUrl} alt="signature" style={{ maxHeight: 54 }} crossOrigin="anonymous" />}
    </div>
    <div style={{ fontSize: 12 }}>Name: {name || ""}</div>
    <div style={{ fontSize: 12 }}>Position: {position || ""}</div>
    <div style={{ fontSize: 12 }}>Date: {date || ""}</div>
  </div>
);

SignatureBlock.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  position: PropTypes.string,
  date: PropTypes.string,
  signatureUrl: PropTypes.string,
};

const PaymentRequestPrint = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { recordData: pr, loading } = useSelector((s) => s.erp_payment_request);

  useEffect(() => {
    dispatch(getPaymentRequest(id));
    return () => dispatch(clearPaymentRequest());
  }, [dispatch, id]);

  if (loading || !pr?.id) {
    return <div className="tw-py-5 tw-text-center"><LoadingSpinner message="Loading..." /></div>;
  }

  const approvals = pr.approvals || [];

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 24, background: "#fff", color: "#000" }}>
      <div className="geist-scope print:tw-hidden tw-mb-3 tw-text-right">
        <button type="button" onClick={() => window.print()}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium tw-border-0 hover:tw-bg-geist-accent-2 tw-transition-colors tw-cursor-pointer">
          <i className="bi bi-printer" /> Print
        </button>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ fontSize: 12 }}>
          <div>Form No: {PAYMENT_REQUEST_FORM_ISO}</div>
          <div>Issue: 01/04/2022</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <h4 style={{ margin: 0 }}>PAYMENT REQUEST FORM</h4>
        </div>
        <div style={{ width: 120 }} />
      </div>

      <table style={{ width: "100%", marginTop: 16, fontSize: 13 }}>
        <tbody>
          <tr>
            <td style={{ width: "70%" }}>Payee*: <strong>{pr.payee}</strong></td>
            <td>Date*: {pr.request_date}</td>
          </tr>
          <tr>
            <td>Employee code: {pr.employee_code}</td>
            <td>Dept: {pr.department}</td>
          </tr>
          <tr>
            <td colSpan={2}>Payment by*: {pr.payment_method}
              {pr.bank_name ? ` — ${pr.bank_name}` : ""}{pr.bank_account ? ` — ${pr.bank_account}` : ""}</td>
          </tr>
        </tbody>
      </table>

      <table border={1} cellPadding={4} style={{ width: "100%", marginTop: 12, fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th>Inv date</th>
            <th>Inv No</th>
            <th>Description</th>
            <th>Cost center</th>
            <th>VND</th>
            <th>USD</th>
            <th>VAT</th>
            <th>TOTAL</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {(pr.items || []).map((i) => (
            <tr key={i.id}>
              <td>{i.inv_date}</td>
              <td>{i.inv_no}</td>
              <td>{i.description}</td>
              <td>{i.cost_center}</td>
              <td style={{ textAlign: "right" }}>{money(i.amount_vnd)}</td>
              <td style={{ textAlign: "right" }}>{money(i.amount_usd)}</td>
              <td style={{ textAlign: "right" }}>{money(i.vat)}</td>
              <td style={{ textAlign: "right" }}>{money(i.total)}</td>
              <td>{i.note}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 600 }}>
            <td colSpan={4} style={{ textAlign: "right" }}>Total payable amount</td>
            <td style={{ textAlign: "right" }}>{money(pr.total_vnd)}</td>
            <td style={{ textAlign: "right" }}>{money(pr.total_usd)}</td>
            <td style={{ textAlign: "right" }}>{money(pr.total_vat)}</td>
            <td style={{ textAlign: "right" }}>{money(pr.total_amount)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 8, fontSize: 13 }}>
        In words: <em>{pr.amount_in_words}</em>
      </div>
      <div style={{ fontSize: 13 }}>Due date: {pr.due_date}</div>

      {/* Signature blocks — row 1: Prepared by + first approver + Accountant */}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <SignatureBlock
          label="Prepared by"
          name={pr.requestor?.full_name || pr.requestor?.name}
          position={pr.requestor?.position}
          date={pr.requestor?.date}
          signatureUrl={pr.requestor?.signature_url}
        />
        {approvals.slice(0, 1).map((a) => (
          <SignatureBlock key={a.id} label={a.block_label}
            name={a.approver_name} position={a.approver_position} date={a.signed_at}
            signatureUrl={a.signature_url} />
        ))}
        <div style={{ border: "1px solid #000", padding: 8, minHeight: 150, flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Accountant</div>
          <div style={{ height: 72 }} />
          <div style={{ fontSize: 12 }}>Name: </div>
          <div style={{ fontSize: 12 }}>Date: </div>
        </div>
      </div>
      {/* Signature blocks — row 2: remaining approvals + Chief Accountant, Finance Director, GM */}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {approvals.slice(1).map((a) => (
          <SignatureBlock key={a.id} label={a.block_label}
            name={a.approver_name} position={a.approver_position} date={a.signed_at}
            signatureUrl={a.signature_url} />
        ))}
        {["Chief Accountant", "Finance Director", "General Manager"].map((label) => (
          <div key={label} style={{ border: "1px solid #000", padding: 8, minHeight: 150, flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{label}</div>
            <div style={{ height: 72 }} />
            <div style={{ fontSize: 12 }}>Name: </div>
            <div style={{ fontSize: 12 }}>Date: </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentRequestPrint;
